"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import type { NavigationItem } from "@/types/database";

interface FullScreenMenuProps {
  isOpen: boolean;
  onClose: () => void;
  menuItems: NavigationItem[];
}

export function FullScreenMenu({ isOpen, onClose, menuItems }: FullScreenMenuProps) {
  const [isVisible, setIsVisible] = useState(false);

  // Handle animation timing
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      document.body.style.overflow = "hidden";
    } else {
      const timer = setTimeout(() => setIsVisible(false), 500); // Match transition duration
      document.body.style.overflow = "";
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isVisible && !isOpen) return null;

  return (
    <div
      className={cn(
        "fixed inset-0 z-[100] flex transition-all duration-500 ease-in-out",
        isOpen ? "pointer-events-auto" : "pointer-events-none",
      )}
    >
      {/* Background Overlay (Darkens the video) */}
      <div
        className={cn(
          "absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-500",
          isOpen ? "opacity-100" : "opacity-0",
        )}
        onClick={onClose}
      />

      {/* Side Menu Panel */}
      <div
        className={cn(
          "relative h-full w-full md:w-[450px] lg:w-[500px] bg-background flex flex-col shadow-2xl transition-transform duration-500 ease-out",
          isOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        {/* Close Button Area */}
        <div className="p-8 md:p-10 flex justify-between items-start">
          {/* Logo or Icon */}
          <div className="flex flex-col gap-1.5 p-2">
            <div className="w-1.5 h-1.5 bg-foreground rounded-full"></div>
            <div className="w-1.5 h-1.5 bg-foreground rounded-full"></div>
            <div className="w-1.5 h-1.5 bg-foreground rounded-full"></div>
            <div className="w-1.5 h-1.5 bg-foreground rounded-full"></div>
          </div>

          {/* Close X */}
          <button
            onClick={onClose}
            className="group p-2 hover:opacity-70 transition-opacity"
          >
            <div className="relative w-6 h-6">
              <span className="absolute top-1/2 left-0 w-full h-0.5 bg-foreground -translate-y-1/2 rotate-45 transform" />
              <span className="absolute top-1/2 left-0 w-full h-0.5 bg-foreground -translate-y-1/2 -rotate-45 transform" />
            </div>
          </button>
        </div>

        {/* Menu Content */}
        <div className="flex-1 overflow-y-auto px-8 md:px-12 pb-12 scrollbar-hide">
          <nav className="flex flex-col gap-6">
            {menuItems.map((item, index) => (
              <div
                key={item.id}
                className={cn(
                  "transform transition-all duration-500 ease-out",
                  isOpen
                    ? "translate-x-0 opacity-100"
                    : "-translate-x-8 opacity-0",
                )}
                style={{ transitionDelay: `${100 + index * 30}ms` }}
              >
                <Link
                  href={item.href}
                  onClick={onClose}
                  className="block group"
                >
                  <span className="font-sans text-xl md:text-2xl text-foreground/80 hover:text-primary transition-colors uppercase tracking-wider font-light">
                    {item.label}
                  </span>

                  {/* Sub-items from database */}
                  {item.children && item.children.length > 0 && (
                    <div className={cn(
                      "mt-3 ml-1 border-l border-border/50 pl-4",
                      item.children.length > 2
                        ? "grid grid-cols-2 gap-x-8 gap-y-2"
                        : "flex flex-col gap-2"
                    )}>
                      {item.children.map((child) => (
                        <Link
                          key={child.id}
                          href={child.href}
                          onClick={onClose}
                          className="text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-primary cursor-pointer transition-colors"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </Link>
              </div>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
}
